import type { Catalog } from "@/lib/i18n/translate";
import type { MessageKey } from "@/lib/i18n/messages.de";

export const en: Catalog<MessageKey> = {
  "app.name": "Rhinvoice",
  "app.skipToContent": "Skip to content",
  "app.loading": "Loading …",
  "app.pageTitle": "{title} · Rhinvoice",

  "nav.main": "Main navigation",
  "nav.dashboard": "Overview",
  "nav.documents": "Documents",
  "nav.invoices": "Invoices",
  "nav.quotes": "Quotes",
  "nav.customers": "Customers",
  "nav.articles": "Articles",
  "nav.settings": "Settings",
  "nav.newInvoice": "New invoice",
  "nav.more": "More",
  "nav.openMenu": "Open menu",
  "nav.collapse": "Collapse sidebar",
  "nav.expand": "Expand sidebar",

  "topbar.search": "Search …",
  "topbar.help": "Help and keyboard shortcuts",
  "topbar.account": "Account",
  "topbar.logout": "Log out",

  "theme.label": "Colour scheme",
  "theme.light": "Light",
  "theme.dark": "Dark",
  "theme.system": "System",
  "language.label": "Language",
  "language.machineTranslated": "Machine translated – feedback welcome",
  "demo.title": "Demo",
  "demo.loginHint":
    "Try Rhinvoice with sample data. All visitors share this demo; it is reset every {minutes} minutes, and no e-mails are sent.",
  "demo.locked":
    "Not available in the public demo: all visitors share one login there, and no e-mails are sent.",
  "demo.signIn": "Open the demo",
  "demo.otherCountries": "Also as a demo:",
  "demo.banner":
    "Sample data and example accounts, please transfer nothing · reset every {minutes} minutes, no e-mails",
  "demo.countryDemo": "Demo {country}",
  "about.title": "About Rhinvoice",
  "about.version": "Version {version}",
  "about.description": "Open-source invoicing for Switzerland, Liechtenstein, Germany and Austria.",
  "about.license": "Licence: GNU Affero General Public License v3",
  "about.source": "Source code",
  "about.reportBug": "Report a problem",
  "support.title": "Report a problem",
  "support.description":
    "Rhinvoice sends nothing by itself. You copy the details and decide where they go.",
  "support.copyStep":
    "1. Copy the log. It can contain names of customers: have a quick look before you send it.",
  "support.copy": "Copy the log",
  "support.copied": "Log copied",
  "support.copyFailed": "Copying did not work. Select the text below and copy it yourself.",
  "support.serverStep": "1. The log is on the server. Run this command there and copy its output:",
  "support.sendStep": "2. Report it on GitHub or send it by mail to {email}.",
  "support.github": "Report on GitHub",
  "about.changelog": "What's new",
  "about.noAdvice": "No tax advice; tax rates without warranty.",
  "about.machineTranslated": "This translation was made by machine. Corrections are welcome:",
  "about.reportTranslation": "Report a translation error",

  "updates.title": "Updates",
  "updates.newVersion": "New version {version}",
  "updates.notes": "What's new?",
  "updates.install": "Update now",
  "updates.installHint": "Takes about half a minute. Your data is backed up first.",
  "updates.installing": "Downloading …",
  "updates.restarting": "Rhinvoice is restarting …",
  "updates.commandEnv": "Set in the .env file:",
  "updates.commandEnvIfPinned": "Only if .env pins a version, set there:",
  "updates.commandOnly": "Run on the server:",
  "updates.commandRun": "Then run on the server:",
  "updates.setupTitle": "{version} needs a new setup",
  "updates.setupHint": "Install it once; your data stays as it is.",
  "updates.setupDownload": "Download the setup",
  "updates.failedTitle": "The update to {version} did not work",
  "updates.failedHint": "Rhinvoice {current} runs again; your data is unchanged.",
  "updates.upToDate": "Rhinvoice is up to date.",
  "updates.notChecked": "Not checked for a new version yet.",
  "updates.check": "Check for updates",
  "updates.checking": "Checking …",
  "updates.setupDownloading": "Downloading … {received} of {total} MB",
  "updates.setupReady": "Downloaded and checked.",
  "updates.setupInstall": "Install",
  "updates.setupFailed": "The download did not work.",
  "updates.setupRetry": "Try again",
  "updates.setupByHand": "Download the setup yourself",
  "updates.closeTitle": "Close Rhinvoice for the update?",
  "updates.closeSetup":
    "Rhinvoice closes, installs {version} and then starts again by itself. This takes about a minute; your data stays as it is.",
  "updates.closeRestart":
    "Rhinvoice restarts for {version}. This takes about half a minute; your data is backed up first.",
  "updates.closeSaveFirst": "Save everything you are working on first.",
  "updates.closeUnsaved":
    "There are unsaved changes. Choose “Later”, save them and start the update again.",
  "updates.closeConfirm": "Close and update",
  "updates.closeAnyway": "Close anyway",
  "updates.later": "Later",
  "updates.closing": "Rhinvoice is closing for the update …",
  "about.madeBy": "made by {maker}",
  "about.legalNotice": "Legal notice",
  "about.privacy": "Privacy",
  "about.openSource": "Open source",

  "palette.title": "Command palette",
  "palette.placeholder": "Type a command or search …",
  "palette.empty": "No results.",
  "palette.actions": "Actions",
  "palette.navigation": "Navigation",
  "palette.toggleTheme": "Toggle colour scheme",
  "palette.shortcuts": "Show keyboard shortcuts",

  "shortcuts.title": "Keyboard shortcuts",
  "shortcuts.description": "Single-key shortcuts only work while no input field is focused.",
  "shortcuts.groupGeneral": "General",
  "shortcuts.groupNavigation": "Navigation",
  "shortcuts.palette": "Open command palette",
  "shortcuts.search": "Focus search",
  "shortcuts.newInvoice": "New invoice",
  "shortcuts.save": "Save",
  "shortcuts.help": "This overview",
  "shortcuts.close": "Close dialog",
  "shortcuts.goDashboard": "Go to overview",
  "shortcuts.goInvoices": "Go to invoices",
  "shortcuts.goCustomers": "Go to customers",
  "shortcuts.goArticles": "Go to articles",
  "shortcuts.goSettings": "Go to settings",
  "shortcuts.then": "then",

  "auth.loginTitle": "Log in",
  "auth.loginDescription": "Log in with your e-mail address.",
  "auth.email": "E-mail address",
  "auth.password": "Password",
  "auth.totpCode": "Verification code",
  "auth.totpHint": "Six-digit code from your authenticator app.",
  "auth.submit": "Log in",
  "auth.submitting": "Logging in …",
  "auth.forgotPassword": "Forgot password?",
  "auth.reset.title": "Forgotten password",
  "auth.reset.description": "You set a new password. Every sign-in with the old one ends.",
  "auth.reset.administrator":
    "Whoever looks after the server sets a new password. In an installation with Docker, in the Rhinvoice folder:",
  "auth.reset.whichAddress": "Which e-mail address? This command shows all accounts:",
  "auth.reset.file":
    "Rhinvoice puts a one-time code into its data folder on this computer and opens the file. It also lists the accounts' e-mail addresses.",
  "auth.reset.request": "Get a code",
  "auth.reset.again": "Get a new code",
  "auth.reset.opened": "The file with the code is open. It lies here:",
  "auth.reset.code": "Code",
  "auth.reset.newPassword": "New password",
  "auth.reset.save": "Save password",
  "auth.reset.done": "The password is changed. Sign in with the new password.",
  "auth.reset.toLogin": "Go to sign-in",
  "auth.reset.back": "Back to sign-in",
  "auth.setupTitle": "Set up Rhinvoice",
  "auth.setupDescription":
    "Create the first user account. It has access to all data of this installation.",
  "auth.displayName": "Your name",
  "auth.newPassword": "Password",
  "auth.passwordHint":
    "At least 12 characters. A sentence of several words is secure and memorable.",
  "auth.setupSubmit": "Create account",
  "auth.loggedOut": "You have been logged out.",
  "auth.sessionExpired": "Your session has expired. Please log in again.",
  "auth.demoReset":
    "The demo was reset, as every {minutes} minutes. This logs everybody out: please log in again.",

  "validation.required": "Please fill in this field.",
  "validation.email": "Please enter a valid e-mail address, e.g. name@company.com.",
  "validation.passwordLength": "The password must be at least 12 characters long.",
  "validation.totp": "The code consists of 6 digits.",
  "validation.summary": {
    one: "1 field needs to be corrected",
    other: "{count} fields need to be corrected",
  },

  "error.demo_locked": "This is not possible in the public demo.",
  "error.update_running": "The update is already being installed.",
  "error.update_check_failed":
    "The information about new versions could not be fetched. Is the computer connected to the internet?",
  "error.update_check_off": "Checking for updates is switched off in this installation.",
  "error.no_update": "There is no update to install here.",
  "error.generic": "Something went wrong. Please try again.",
  "error.network": "No connection to the server. Please check your connection.",
  "error.authentication_failed": "E-mail address or password is incorrect.",
  "error.session_expired": "Your session has expired. Please log in again.",
  "error.not_logged_in": "You are not logged in.",
  "error.reset_code_invalid":
    "The e-mail address or the code is wrong, or the code has expired. After five wrong tries a new code is needed.",
  "error.reset_by_administrator": "Here an administrator resets the password on the server.",
  "error.rate_limited": "Too many attempts from this address. Please wait a minute.",
  "error.account_locked": "Too many failed attempts. The account is locked for 15 minutes.",
  "error.totp_required": "Please enter the verification code.",
  "error.retry": "Try again",
  "error.mail_not_configured": "Sending e-mails is not set up yet (Settings → E-mail).",
  "error.mail_draft": "Drafts are not sent. Please issue the document first.",
  "error.mail_auth_failed": "The mail server refused the user name or password.",
  "error.mail_recipient_refused": "The mail server refused a recipient. Please check the address.",
  "error.mail_sender_refused":
    "The mail server does not accept the sender address. It usually has to belong to the account.",
  "error.mail_tls_failed":
    "The encrypted connection to the mail server failed. Please check port and encryption.",
  "error.mail_timeout": "The mail server did not answer in time.",
  "error.mail_connection_failed": "No connection to the mail server. Please check server and port.",
  "error.mail_failed": "The mail server did not accept the e-mail.",
  "error.recipient_missing": "Please enter at least one e-mail address.",
  "error.invalid_recipient": "A recipient address is invalid.",
  "error.mail_partly_refused":
    "The mail server refused some recipients; the others received the e-mail (noted in the history).",
  "error.mail_too_large": "The e-mail is larger than the mail server takes.",
  "error.mail_utf8_unsupported": "The mail server takes no addresses with umlauts or accents.",
  "error.mail_login_needs_tls": "A login needs an encrypted connection (STARTTLS or SSL/TLS).",
  "error.mail_password_required":
    "For another server, port or user, please enter the password again.",
  "error.reminder_not_open": "The invoice is no longer open; its reminders are not sent any more.",
  "error.refusedRecipients": "Refused: {refused}.",
  "error.serverSaid": "The server said: «{reply}»",
  "error.not_found": "Not found. It may have been deleted in the meantime.",
  "error.invalid_state":
    "This action is not possible in the current status. Please reload the page.",
  "error.bank_account_missing":
    "There is no bank account for this currency. Please add one under Settings → Bank accounts.",
  "error.bank_account_unknown": "This bank account no longer exists. Please choose another one.",
  "error.bank_account_currency": "The bank account is in a different currency than the document.",
  "error.holder_address_missing":
    "A third party's account needs the address of the account holder.",
  "error.no_positions": "The document needs at least one line with an amount.",
  "error.non_positive_total":
    "The total of an invoice must be greater than zero. Credits are created by cancelling.",
  "error.due_before_issue": "The due date is before the date.",
  "error.reverse_charge_needs_vat_number":
    "Reverse charge needs the customer's VAT number. Please add it to the customer.",
  "error.company_vat_number_missing":
    "The invoice must state your VAT number. Please add it under Settings → Company, or choose “Not VAT registered / small business” there.",
  "error.company_tax_id_missing":
    "The invoice must state your tax number or VAT ID. Please add it under Settings → Company.",
  "error.company_vat_id_missing":
    "An intra-Community supply states your VAT ID. Please add it under Settings → Company.",
  "error.customer_vat_id_missing":
    "An intra-Community supply states the customer's VAT ID. Please add it to the customer.",
  "error.goods_only_treatment":
    "Intra-Community supplies and export deliveries apply to goods only. Choose «Reverse charge» or «Export» for the document.",
  "error.customer_vat_number_required":
    "Invoices above EUR 10,000 to businesses in Austria must state the customer's VAT ID. Please add it to the customer.",
  "error.issue_date_in_future": "The date must not be in the future.",
  "error.issue_date_before_last":
    "The date is before the last issued document. Numbers and dates must ascend.",
  "error.payment_exceeds_open_amount": "The amount is higher than the open amount.",
  "error.payment_date_in_future": "The payment date must not be in the future.",
  "error.write_off_date_in_future": "The date of the write-off must not be in the future.",
  "error.payment_dated_before_write_off":
    "The payment is dated before the write-off. Take the write-off back instead.",
  "error.written_off": "The invoice was written off. Take the write-off back first.",
  "error.clock_went_back": "The server's clock is behind. Please try again in a moment.",
  "error.write_off_before_invoice":
    "The date of the write-off must not be before the invoice date.",
  "error.payment_before_write_off":
    "This payment came in before the write-off. Take the write-off back first.",
  "error.unknown_placeholder":
    "Unknown placeholder. Available are {nummer}, {datum}, {faellig}, {kunde}, {betrag}, {waehrung}, {firma}, {kontoinhaber} and {kontoinhaber_adresse}.",
  "error.already_converted": "An invoice has already been created from this quote.",
  "error.pageDescription": "This page could not be shown. Your saved data is not affected.",

  "dashboard.title": "Overview",
  "dashboard.welcome": "Welcome, {name}",
  "dashboard.emptyTitle": "No invoices yet",
  "dashboard.emptyDescription":
    "As soon as you issue invoices, you will see open, overdue and paid amounts here.",

  "settings.title": "Settings",
  "settings.account": "Account",
  "settings.security": "Security",
  "settings.interaction": "Interaction",
  "settings.singleKeyShortcuts": "Single-key shortcuts",
  "settings.singleKeyShortcutsHint":
    "Shortcuts such as N (new invoice) or G then C (customers). Turn them off if you use speech input or assistive technology. The command palette ({mod} K) stays available.",
  "settings.changePassword": "Change password",
  "settings.currentPassword": "Current password",
  "settings.newPassword": "New password",
  "settings.passwordChanged": "Password changed. Other sessions have been logged out.",
  "settings.totpTitle": "Two-factor authentication",
  "settings.totpEnabled": "Enabled",
  "settings.totpDisabled": "Not enabled",
  "settings.totpDescription": "Protects your account with a code from an authenticator app.",
  "settings.totpEnable": "Set up",
  "settings.totpDisable": "Disable",
  "settings.totpScan": "Add this key to your authenticator app, then enter the code it shows.",
  "settings.totpSecret": "Key",
  "settings.totpConfirm": "Confirm",
  "settings.totpActivated": "Two-factor authentication is enabled.",
  "settings.totpDeactivated": "Two-factor authentication is disabled.",
  "settings.totpDisableConfirm": "Please confirm with your password and a current code.",

  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.optional": "(optional)",
  "common.copy": "Copy",
  "common.copied": "Copied",

  "common.new": "New",
  "common.edit": "Edit",
  "common.archive": "Archive",
  "common.restore": "Restore",
  "common.archived": "Archived",
  "common.delete": "Delete",
  "common.saved": "Saved",
  "common.saving": "Saving …",
  "common.back": "Back",
  "common.previous": "Previous page",
  "common.next": "Next page",
  "common.pageInfo": "{from}–{to} of {total}",
  "common.showArchived": "Show archived",
  "common.undo": "Undo",
  "common.opensInNewTab": " (opens in a new tab)",
  "common.loadError": "The data could not be loaded.",
  "common.none": "—",
  "common.default": "Default",
  "common.notSet": "Not set",
  "common.useDefault": "Use default",
  "common.saveShortcut": "Save ({mod} ↵)",
  "common.unsavedChanges": "Unsaved changes",
  "common.results": { zero: "No results", one: "1 result", other: "{count} results" },
  "address.legend": "Address",
  "address.street": "Street",
  "address.houseNumber": "No.",
  "address.supplement": "Additional line",
  "address.supplementHint": "e.g. c/o, P.O. box or department",
  "address.postalCode": "Postal code",
  "address.city": "City",
  "address.country": "Country",
  "validation.maxLength": "At most {max} characters.",
  "validation.number": "Please enter a number, e.g. {example}.",
  "validation.range": "Please enter a number from {min} to {max}.",
  "validation.decimals": "At most {max} decimal places.",
  "validation.percent": "Please enter a value between 0 and 100.",
  "validation.days": "Please enter a whole number from 0 to 365.",
  "validation.positiveInteger": "Please enter a positive whole number.",
  "validation.iban.format":
    "An IBAN starts with the country code and two check digits, e.g. CH93 0076 …",
  "validation.iban.country": "IBANs from this country are not supported.",
  "validation.iban.length": "An IBAN from {country} has {length} characters – please check.",
  "validation.iban.checksum": "The check digits do not match – please check for typos.",
  "validation.iban.isQr": "This is a QR-IBAN. Enter it as QR-IBAN and the regular IBAN here.",
  "validation.iban.notQr": "A QR-IBAN has a number between 30000 and 31999 at positions 5–9.",
  "validation.bic": "A BIC has 8 or 11 characters, e.g. UBSWCHZH80A.",
  "validation.vat.swiss-format": "A UID has the form CHE-123.456.789.",
  "validation.vat.swiss-check-digit": "The check digit of the UID is wrong – please check.",
  "validation.vat.german-format": "A German VAT number has the form DE123456789.",
  "validation.vat.austrian-format": "An Austrian VAT number has the form ATU12345678.",
  "validation.numberTaken": "This number is already taken.",
  "validation.server": "Please check this value ({message}).",
  "customers.title": "Customers",
  "customers.new": "New customer",
  "customers.edit": "Edit customer",
  "customers.emptyTitle": "No customers yet",
  "customers.emptyDescription":
    "Create your first customer. The address and terms are used for invoices later.",
  "customers.noResults": "No customers match this search.",
  "customers.resetSearch": "Reset search",
  "customers.searchLabel": "Search customers",
  "customers.searchPlaceholder": "Name, number, city, e-mail …",
  "customers.number": "No.",
  "customers.numberAndSince": "Customer number {number} · customer since {since}",
  "customers.numberLabel": "Customer number",
  "customers.numberHint": "Leave empty for the next free number.",
  "customers.name": "Name",
  "customers.city": "City",
  "customers.email": "E-mail",
  "customers.group": "Group",
  "customers.kind": "Type",
  "customers.kindCompany": "Company",
  "customers.kindPerson": "Person",
  "customers.sectionBasics": "Basic data",
  "customers.sectionContact": "Contact",
  "customers.sectionBilling": "Billing",
  "customers.sectionDelivery": "Delivery address",
  "customers.companyName": "Company name",
  "customers.salutation": "Salutation",
  "customers.salutationHint": "e.g. Mr, Ms or empty",
  "customers.firstName": "First name",
  "customers.lastName": "Last name",
  "customers.contactPerson": "Contact person",
  "customers.contactPersonHint": "Printed as attention line in the address.",
  "customers.phone": "Phone",
  "customers.vatNumber": "VAT number",
  "customers.vatNumberHint": "Required for reverse charge with business customers in the EU.",
  "customers.buyerReference": "Leitweg-ID or fixed buyer reference",
  "customers.buyerReferenceHint":
    "For the XRechnung. Authorities: their Leitweg-ID. Businesses: only fill this in if they always want the same reference. If the reference changes with every order (e.g. the order number), leave this field empty and enter it in the invoice under “More details → Reference”.",
  "customers.defaultCurrency": "Default currency",
  "customers.defaultLanguage": "Document language",
  "customers.taxExempt": "Tax exempt",
  "customers.taxExemptHint":
    "Only for customers exempt from VAT, e.g. embassies or international organisations. Not for businesses abroad: a business in the EU with a VAT number (e.g. ATU12345678) gets reverse charge automatically, one outside the EU an invoice without VAT (export).",
  "customers.taxExemptReverseCharge":
    "This business has an EU VAT number: without “Tax exempt” it gets reverse charge automatically. With “Tax exempt” the invoice says “VAT exempt supply” instead.",
  "customers.defaultDiscount": "Default discount in %",
  "customers.paymentTerms": "Payment terms in days",
  "customers.paymentTermsHint": "Leave empty to use the company's payment terms.",
  "customers.hasDelivery": "Different delivery address",
  "customers.note": "Internal note",
  "customers.noteHint": "Not printed on documents.",
  "customers.created": "Customer “{name}” created.",
  "customers.sheetHint": "The invoice stays open behind this. After saving, you continue there.",
  "customers.createAndChoose": "Create and choose",
  "customers.archivedToast": "“{name}” archived.",
  "customers.restoredToast": "“{name}” restored.",
  "customers.archivedNotice": "This customer is archived and hidden from selection lists.",
  "customers.documentsTitle": "Documents",
  "customers.documentsEmpty": "There are no documents for this customer yet.",
  "articles.title": "Articles",
  "articles.new": "New article",
  "articles.edit": "Edit article",
  "articles.emptyTitle": "No articles yet",
  "articles.emptyDescription":
    "Add products and services with price and unit to insert them into invoices quickly.",
  "articles.noResults": "No articles match this search.",
  "articles.searchLabel": "Search articles",
  "articles.searchPlaceholder": "Name, number, description …",
  "articles.number": "No.",
  "articles.numberLabel": "Article number",
  "articles.name": "Name",
  "articles.description": "Description",
  "articles.descriptionHint": "Printed below the name on documents.",
  "articles.group": "Group",
  "articles.unit": "Unit",
  "articles.taxCategory": "Tax rate",
  "articles.supply": "Goods or service",
  "articles.supplyHint":
    "Goods cross borders under other VAT rules than services (export, intra-Community supply).",
  "articles.price": "Price",
  "articles.prices": "Prices (net)",
  "articles.pricesHint": "Leave empty if the article is not offered in this currency.",
  "articles.priceIn": "Price in {currency}",
  "articles.created": "Article “{name}” created.",
  "articles.archivedToast": "“{name}” archived.",
  "articles.restoredToast": "“{name}” restored.",
  "articles.archivedNotice": "This article is archived and hidden from selection lists.",
  "tax.standard": "Standard rate",
  "tax.reduced": "Reduced rate",
  "tax.special": "Special rate",
  "tax.zero": "0 % (taxable)",
  "tax.exempt": "Exempt",
  "supply.service": "Service",
  "supply.goods": "Goods",
  "settings.tabMail": "E-mail",
  "settings.tabCompany": "Company",
  "settings.tabBankAccounts": "Bank accounts",
  "settings.tabNumbering": "Number sequences",
  "settings.tabTexts": "Texts",
  "texts.intro":
    "Text templates fill new documents automatically. Templates marked as default are inserted; all others can be chosen in the editor.",
  "texts.documentType": "Document type",
  "texts.kind.intro": "Intro",
  "texts.kind.payment": "Payment terms",
  "texts.kind.closing": "Closing",
  "texts.kindHint.intro": "Before the lines, e.g. thanks for the order.",
  "texts.kindHint.payment":
    "After the totals. Several default templates are put one below the other.",
  "texts.kindHint.closing": "At the end of the document, e.g. greetings.",
  "texts.add": "Add",
  "texts.addTitle": "New text template",
  "texts.editTitle": "Edit text template",
  "texts.empty": "No text templates yet.",
  "texts.default": "Default",
  "texts.moveUp": "Move “{name}” up",
  "texts.moveDown": "Move “{name}” down",
  "texts.edit": "Edit “{name}”",
  "texts.delete": "Delete “{name}”",
  "texts.deleteTitle": "Delete text template?",
  "texts.deleteDescription":
    "“{name}” will be deleted. Documents that already contain the text stay unchanged.",
  "texts.deleted": "Text template deleted",
  "texts.name": "Name",
  "texts.nameHint": "Short; this is how it appears in the editor, e.g. “Payable immediately”.",
  "texts.text": "Text",
  "texts.placeholders": "Insert placeholder",
  "texts.insertPlaceholder": "Insert placeholder for {placeholder}",
  "texts.placeholder.nummer": "number",
  "texts.placeholder.datum": "date",
  "texts.placeholder.faellig": "due date or validity",
  "texts.placeholder.kunde": "customer",
  "texts.placeholder.betrag": "amount",
  "texts.placeholder.waehrung": "currency",
  "texts.placeholder.firma": "your company",
  "texts.placeholder.kontoinhaber": "account holder",
  "texts.placeholder.kontoinhaber_adresse": "account holder's address",
  "texts.useAsDefault": "Insert into new documents (default)",
  "texts.notices": "Legal notices",
  "texts.noticesHint":
    "Appear automatically depending on the tax treatment. The wording is stored with a document when it is issued. Not tax advice: please have it checked.",
  "texts.noticeBuiltIn": "Suggested wording for your country.",
  "texts.noticeCustomized": "Your own wording.",
  "texts.restoreNotice": "Restore suggestion",
  "settings.tabSecurity": "Security",
  "settings.tabInteraction": "Interaction",
  "company.title": "Company",
  "company.description": "These details appear as the sender on all documents.",
  "company.setupHint": "Please enter your company details first.",
  "company.name": "Company name",
  "company.sectionContact": "Contact",
  "company.sectionTax": "Tax and register",
  "company.sectionDefaults": "Defaults",
  "company.sectionAppearance": "Appearance",
  "company.email": "E-mail",
  "company.phone": "Phone",
  "company.website": "Website",
  "company.vatNumber": "VAT number",
  "company.vatNumberHint": "e.g. CHE-123.456.789 MWST or DE123456789",
  "company.taxNumber": "Tax number",
  "company.taxNumberHint": "German tax number if there is no VAT number.",
  "company.taxDialogTitle": "Company tax details",
  "company.taxDialogHint":
    "These details appear on every invoice and apply to the whole company from now on.",
  "company.taxDialogOtherErrors":
    "Other company details are incomplete. Please check them under Settings → Company.",
  "company.commercialRegister": "Commercial register",
  "company.commercialRegisterHint": "e.g. register court and number",
  "company.smallBusiness": "Not VAT registered / small business",
  "company.smallBusinessHint": "Invoices then contain no VAT but the legally required notice.",
  "company.cashRounding": "Cash rounding",
  "company.cashRoundingHint": "Round CHF totals to 0.05.",
  "company.defaultCurrency": "Default currency",
  "company.defaultLanguage": "Document language",
  "company.paymentTerms": "Payment terms in days",
  "company.accentHue": "Accent colour",
  "company.accentHueHint": "Hue of the interface (0–359). Every hue is checked for readability.",
  "company.saved": "Company details saved.",
  "company.logo": "Logo",
  "company.logoHint": "PNG, JPEG or WebP, at most 1 MB. Printed at the top of documents.",
  "company.logoUpload": "Upload logo",
  "company.logoReplace": "Replace logo",
  "company.logoRemove": "Remove logo",
  "company.logoAlt": "Current company logo",
  "company.logoUploaded": "Logo saved.",
  "company.logoRemoved": "Logo removed.",
  "company.logoTooLarge": "The logo must be at most 1 MB.",
  "language.de": "German",
  "language.en": "English",
  "language.fr": "French",
  "language.it": "Italian",
  "bank.title": "Bank accounts",
  "bank.description":
    "Accounts for payment details and QR codes. One account per currency is the default; a document can choose another.",
  "bank.add": "Add account",
  "bank.edit": "Edit account",
  "bank.emptyTitle": "No bank account yet",
  "bank.emptyDescription": "Add at least one account per invoice currency.",
  "bank.bankName": "Bank",
  "bank.holder": "Account holder",
  "bank.iban": "IBAN",
  "bank.qrIban": "QR-IBAN",
  "bank.qrIbanHint": "Only for QR-bills with QR reference (Switzerland and Liechtenstein).",
  "bank.bic": "BIC / SWIFT",
  "bank.bicEpcHint":
    "Needed for the EPC QR code on euro invoices to customers in the EU: without the BIC there is none.",
  "bank.currency": "Currency",
  "bank.isDefault": "Default account for this currency",
  "bank.thirdParty": "Account belongs to a third party (e.g. a financial service provider)",
  "bank.thirdPartyHint":
    "Documents paid to this account name the account holder as payee. Their address appears on the QR payment part.",
  "bank.thirdPartyBadge": "Third-party account",
  "bank.holderAddress": "Address of the account holder",
  "bank.saved": "Bank account saved.",
  "bank.deleteTitle": "Delete bank account?",
  "bank.deleteDescription":
    "Issued documents keep their account details. Drafts and new documents use the default account of their currency.",
  "bank.deleted": "Bank account deleted.",
  "numbering.title": "Number sequences",
  "numbering.description": "Invoice numbers are assigned only when issuing – gapless and unique.",
  "numbering.kind.invoice": "Invoices",
  "numbering.kind.quote": "Quotes",
  "numbering.kind.cancellation": "Cancellations",
  "numbering.kind.customer": "Customers",
  "numbering.kind.article": "Articles",
  "numbering.pattern": "Pattern",
  "numbering.patternHint":
    "{YYYY} year, {YY} two-digit year, {MM} month, {NNNN} counter (number of N = minimum digits).",
  "numbering.nextValue": "Next counter",
  "numbering.nextValueHint": "To continue seamlessly from a previous program.",
  "numbering.startValue": "Start value after year change",
  "numbering.yearlyReset": "Reset every year",
  "numbering.preview": "Next number",
  "numbering.saved": "Number sequence saved.",
  "numbering.problem.length": "The pattern must have 1 to 40 characters.",
  "numbering.problem.placeholder": "Unknown placeholder. Allowed are {YYYY}, {YY}, {MM} and {N…}.",
  "numbering.problem.counter": "The pattern needs exactly one counter such as {NNNN}.",
  "numbering.problem.characters": "Allowed are letters, digits and - _ / . plus the placeholders.",
  "numbering.problem.year":
    "A yearly reset needs {YYYY} or {YY} in the pattern, otherwise numbers repeat.",
  "shortcuts.newQuote": "New quote",
  "shortcuts.newCustomer": "New customer",
  "shortcuts.newArticle": "New article",
  "shortcuts.or": "or",
  "shortcuts.undo": "Undo the last action (while its message is visible)",
  "shortcuts.payment": "Record a payment (in a document)",
  "shortcuts.moveLineUp": "Move line up (in the editor)",
  "shortcuts.moveLineDown": "Move line down (in the editor)",
  "shortcuts.goQuotes": "Go to quotes",

  "dashboard.asTable": "As table",
  "dashboard.currency": "Currency",
  "dashboard.revenueYear": "Revenue {year}",
  "dashboard.vsLastYear": "against the same period last year",
  "dashboard.noComparison": "no previous year",
  "dashboard.showInvoices": "View invoices",
  "dashboard.showOpen": "Open invoices",
  "dashboard.showDunning": "Go to dunning",
  "dashboard.showQuotes": "View open quotes",
  "dashboard.daysToPay": {
    zero: "Customers pay the same day",
    one: "Paid after 1 day on average",
    other: "Paid after {count} days on average",
  },
  "dashboard.oldestOverdue": {
    zero: "due today",
    one: "oldest 1 day late",
    other: "oldest {count} days late",
  },
  "dashboard.openQuotes": "Open quotes",
  "dashboard.quoteCount": { zero: "No quotes", one: "1 quote", other: "{count} quotes" },
  "dashboard.acceptance": "{rate} accepted this year",
  "dashboard.noDecisions": "No quotes decided yet",
  "dashboard.aging": "Open by due date",
  "dashboard.nothingOpen": "No open invoices.",
  "dashboard.age.not_due": "Not due yet",
  "dashboard.age.late_30": "1–30 days overdue",
  "dashboard.age.late_60": "31–60 days overdue",
  "dashboard.age.late_more": "More than 60 days overdue",
  "dashboard.overdueList": "Longest overdue",
  "dashboard.noOverdue": "No overdue invoices.",
  "dashboard.daysLate": { zero: "due today", one: "1 day overdue", other: "{count} days overdue" },
  "dashboard.remind": "Go to dunning",
  "dashboard.topCustomers": "Top customers {year}",
  "dashboard.topCustomersHint": "Net revenue, less cancellations.",
  "dashboard.noCustomerRevenue": "No revenue this year yet.",
  "dashboard.expected": "Expected payments",
  "dashboard.expectedHint":
    "Open invoices by week of their due date; this week includes everything overdue.",
  "dashboard.week": "Week",
  "dashboard.weekLabel": "Wk {week}",
  "dashboard.amount": "Amount",
  "dashboard.figuresIn": "Figures in {currency}",
  "dashboard.open": "Open",
  "dashboard.openCount": {
    zero: "No open invoices",
    one: "1 open invoice",
    other: "{count} open invoices",
  },
  "dashboard.overdue": "Overdue",
  "dashboard.overdueCount": {
    zero: "Nothing overdue",
    one: "1 overdue invoice",
    other: "{count} overdue invoices",
  },
  "dashboard.paid": "Paid",
  "dashboard.paidHint": "Sum of all payments received",
  "dashboard.revenue": "Revenue per month",
  "dashboard.revenueHint": "Net revenue in {currency}, less cancellations.",
  "dashboard.revenueIn": "Revenue in {currency}",
  "dashboard.noRevenue": "No invoices were issued in the last 12 months.",
  "dashboard.month": "Month",
  "dashboard.netRevenue": "Net revenue",
  "dashboard.activity": "Recent activity",
  "dashboard.noActivity": "No activity yet.",

  "documentType.invoice": "Invoice",
  "documentType.quote": "Quote",
  "documentType.cancellation": "Cancellation invoice",

  "status.draft": "Draft",
  "status.sent": "Sent",
  "status.accepted": "Accepted",
  "status.declined": "Declined",
  "status.expired": "Expired",
  "status.issued": "Issued",
  "status.partially_paid": "Partially paid",
  "status.overdue": "Overdue",
  "status.overdueDays": {
    zero: "Overdue",
    one: "Overdue · 1 day",
    other: "Overdue · {count} days",
  },
  "status.paid": "Paid",
  "status.cancelled": "Cancelled",
  "status.written_off": "Written off",

  "event.emailed": "Sent by e-mail",
  "event.reminder_emailed": "Reminder sent by e-mail",
  "event.created": "Created",
  "event.created_from": "Created from",
  "event.issued": "Issued",
  "event.sent": "Sent",
  "event.accepted": "Accepted",
  "event.declined": "Declined",
  "event.converted": "Turned into an invoice",
  "event.payment_added": "Payment recorded",
  "event.payment_removed": "Payment removed",
  "event.cancelled": "Cancelled",
  "event.tax_overridden": "Tax treatment chosen manually",
  "event.reminded": "Reminder written",
  "event.imported": "Taken over from a program used before",
  "event.file_added": "File added",
  "event.written_off": "Written off",
  "event.write_off_removed": "Write-off taken back",

  "treatment.domestic": "Domestic (taxable)",
  "treatment.reverse_charge": "Reverse charge",
  "treatment.export": "Export (tax free)",
  "treatment.small_business": "Small business (no tax)",
  "treatment.exempt": "Exempt from tax",
  "treatment.intra_community": "Intra-Community supply (tax free)",
  "treatment.goods_export": "Export delivery (tax free)",

  "documents.newInvoice": "New invoice",
  "documents.newQuote": "New quote",
  "documents.draftTitle": "{kind} (draft)",
  "documents.numberedTitle": "{kind} {number}",
  "documents.searchLabel": "Search documents",
  "documents.searchPlaceholder": "Number, customer, subject, line …",
  "documents.views": "View",
  "documents.view.all": "All",
  "documents.view.draft": "Drafts",
  "documents.view.open": "Open",
  "documents.view.overdue": "Overdue",
  "documents.view.paid": "Paid",
  "documents.view.cancelled": "Cancelled",
  "documents.view.written_off": "Written off",
  "documents.view.sent": "Sent",
  "documents.view.accepted": "Accepted",
  "documents.view.declined": "Declined",
  "documents.view.expired": "Expired",
  "documents.noResults": "No documents for this filter.",
  "documents.resetFilter": "Reset filter",
  "documents.emptyInvoices": "No invoices yet",
  "documents.emptyInvoicesDescription":
    "Create your first invoice. Its number is assigned when you issue it.",
  "documents.emptyQuotes": "No quotes yet",
  "documents.emptyQuotesDescription":
    "Create a quote and turn it into an invoice with one click once it is accepted.",
  "documents.number": "Number",
  "documents.status": "Status",
  "documents.amount": "Amount",
  "documents.openShort": "{amount} open",
  "documents.applyLine": "Apply",
  "documents.customerNumber": "Customer no. {number}",
  "documents.customerSearch": "Find customer",
  "documents.changeCustomer": "Change",
  "documents.discardLine": "Discard",
  "documents.saveAsArticle": "Save as article",
  "documents.articleSaved": "Article {number} “{name}” saved.",
  "documents.dueInDays": "{days} days",
  "documents.dueNow": "Immediately",
  "documents.editLine": "Edit line {number}: {description}",
  "documents.editPane": "Edit",
  "documents.insertTemplate": "Insert template …",
  "documents.linePriceIn": "Price {currency}",
  "documents.manageTemplates": "Manage text templates",
  "documents.netTotal": "Net {amount}",
  "documents.noLines": "No lines yet. Add one; articles are suggested while you type.",
  "documents.noticeAuto": "Automatic notice:",
  "documents.paneSwitch": "View",
  "documents.paymentTemplates": "Payment terms from text templates",
  "documents.paymentText": "Payment terms",
  "documents.payWithin": "Payable within",
  "documents.placeholdersHint":
    "Placeholders for number, due date, customer and amount are filled in when printing.",
  "documents.previewHint":
    "The preview shows the document as printed. Clicking a line opens it in the list.",
  "documents.previewPane": "Preview",
  "documents.cannotMarkPaid":
    "{number} cannot be marked as paid (paid, cancelled or a draft meanwhile). Nothing was changed.",
  "documents.popupBlocked":
    "The browser blocked the new window. Please allow pop-ups for Rhinvoice or use “Download”.",
  "error.unavailable":
    "The PDF service is not reachable right now. Please try again later or contact whoever runs Rhinvoice.",
  "error.cannot_mark_paid":
    "One of the selected invoices cannot be marked as paid (paid or cancelled meanwhile). Nothing was changed.",
  "documents.autosaveFailed": "Saving automatically failed. Please save by hand.",
  "documents.pdf": "PDF",
  "documents.download": "Download",
  "documents.print": "Print",
  "documents.share": "Share",
  "documents.viewMode": "Display",
  "documents.viewDocument": "Document",
  "documents.viewDetails": "Details",
  "documents.imported": "Taken over",
  "documents.importedHint":
    "Taken over from a program used before. The PDF is the invoice; it has no positions.",
  "documents.originalPdf": "Original PDF",
  "documents.noPdf": "No PDF yet",
  "documents.noPdfHint":
    "No PDF was found for this invoice when it was taken over. Add it to make the invoice complete; it cannot be replaced afterwards.",
  "documents.addPdf": "Add PDF",
  "documents.attachmentsTitle": "Attachments",
  "documents.attachmentsHint": "Reminders, corrections and copies from the program used before.",
  "documents.attachmentsEmpty": "No attachments.",
  "documents.addPdfTitle": "Add the PDF?",
  "documents.addAttachmentTitle": "Attach the file?",
  "documents.addFileConfirm":
    "«{name}» is kept with this invoice for good. It cannot be replaced or removed afterwards.",
  "documents.addAttachment": "Attach file",
  "documents.fileAdded": "File added.",
  "documents.pdfAdded": "PDF added.",
  "documents.addFileHint": "PDF only, at most 20 MB. Added files cannot be removed.",
  "documents.paidOnUnknown":
    "Payment date not known from the program used before; taken as the due date.",
  "documents.pdfLoading": "Loading the PDF …",
  "documents.previewEmpty": "The preview appears as soon as a customer and a line are entered.",
  "documents.previewTitle": "Preview: {title}",
  "documents.reverseChargeHint":
    "Reverse charge: no {tax}. The notice and the customer's VAT number are added automatically.",
  "documents.sectionMore": "More details",
  "documents.sectionMoreHint": "Subject, reference, service period, currency, discount",
  "documents.sectionTexts": "Texts",
  "documents.sectionTextsHint": "Intro, payment terms, closing",
  "documents.filters": "Filters",
  "documents.year": "Year",
  "documents.allYears": "All years",
  "documents.customerFilter": "Customer",
  "documents.allCustomers": "All customers",
  "documents.removeCustomerFilter": "Remove customer filter",
  "documents.selectDocument": "Select {number}",
  "documents.selectAllPayable": "Select all open invoices on this page",
  "documents.markPaidShort": "Paid",
  "documents.markPaidRow": "Mark {number} as paid (today, full amount)",
  "documents.selection": "Selection",
  "documents.selectedCount": { one: "1 selected", other: "{count} selected" },
  "documents.markSelectedPaid": "Mark as paid",
  "documents.clearSelection": "Clear selection",
  "documents.markedPaidNumber": "{number} marked as paid",
  "documents.markedPaidMany": "{count} invoices marked as paid",
  "documents.paymentsUndone": "Payments removed",
  "documents.sums": "Totals",
  "documents.sumCountInvoices": {
    one: "1 invoice in {currency}",
    other: "{count} invoices in {currency}",
  },
  "documents.sumCountQuotes": {
    one: "1 quote in {currency}",
    other: "{count} quotes in {currency}",
  },
  "documents.sumTotal": "Total {amount}",
  "documents.sumOpen": "{amount} of it open",
  "documents.companyMissingTitle": "Company details missing",
  "documents.companyMissing":
    "Please enter your company details first. They appear as the sender on every document.",
  "documents.facts": "Key facts",

  "documents.sectionRecipient": "Recipient and subject",
  "documents.customer": "Customer",
  "documents.customerPlaceholder": "Type a name, number or city …",
  "documents.customerRequired": "Please choose a customer.",
  "documents.noCustomerFound": "No customer found.",
  "documents.createCustomerNamed": "Create “{name}” as a new customer",
  "documents.noCustomersTitle": "No customers yet.",
  "documents.noCustomersHint":
    "An invoice needs a recipient. Create one here; the invoice stays open.",
  "documents.createCustomer": "Create customer",
  "documents.importCustomers": "Import customers",
  "documents.blockersTitle": "Still missing before issuing:",
  "documents.blockersHint": "Saving works already; only issuing waits for this.",
  "blocker.bank_account_missing": "a bank account in {currency} for the payment details",
  "blocker.reverse_charge_needs_vat_number": "the customer’s VAT number for reverse charge",
  "blocker.company_vat_number_missing": "your VAT number, or that you are not VAT registered",
  "blocker.company_tax_id_missing": "your tax number or VAT ID",
  "blocker.customer_vat_number_required": "the customer’s VAT number (invoices above EUR 10,000)",
  "blocker.company_vat_id_missing": "your VAT ID for the intra-Community supply",
  "blocker.customer_vat_id_missing": "the customer’s VAT ID for the intra-Community supply",
  "blocker.fix.bank_account": "Add {currency} account",
  "blocker.fix.company": "Add tax details",
  "blocker.fix.customer": "Complete customer",
  "documents.subject": "Subject",
  "documents.subjectHint": "e.g. “Website relaunch, phase 2”",
  "documents.reference": "Reference",
  "documents.referenceHint": "e.g. the customer's order number",
  "documents.referenceHintXRechnung": "Order number or similar – needed for the XRechnung",
  "documents.referenceHintFixed": "The XRechnung uses the customer's Leitweg-ID: {value}",
  "documents.currency": "Currency",
  "documents.language": "Language",
  "documents.sectionDates": "Dates",
  "documents.issueDate": "Date",
  "documents.issueDateHint": "Set to today. Leave empty to use the date of issue.",
  "documents.dueDate": "Due date",
  "documents.bankAccount": "Bank details",
  "documents.bankAccountHint": "The account this document is paid to.",
  "documents.bankAccountDefault": "(default)",
  "documents.bankAccountThirdParty": "(third party)",
  "documents.dueDateHint": "Leave empty to use the payment terms of the customer or company.",
  "documents.validUntil": "Valid until",
  "documents.validUntilHint": "Leave empty for 30 days from the date.",
  "documents.serviceFrom": "Service from",
  "documents.serviceTo": "Service until",
  "documents.serviceHint": "Determines the tax rate; without it the date applies.",
  "documents.servicePeriod": "Service period",
  "documents.dueBeforeIssue": "The due date is before the date.",
  "documents.serviceEndsBeforeStart": "The service period ends before it starts.",
  "documents.introText": "Introduction",
  "documents.closingText": "Closing text",
  "documents.closingTextHint": "e.g. thanks, payment notes or how long the quote is valid.",

  "documents.sectionLines": "Lines",
  "documents.priceMode": "Prices",
  "documents.priceModeNet": "Net (plus {tax})",
  "documents.priceModeGross": "Gross (incl. {tax})",
  "documents.lineLabel": "Line {number}",
  "documents.lineError": "Line {number}: {message}",
  "documents.lineDescription": "Description",
  "documents.lineText": "Text",
  "documents.lineTextPlaceholder": "Heading or note",
  "documents.lineDetails": "Details of line {number}",
  "documents.lineDetailsPlaceholder": "Details (shown below the description)",
  "documents.addDetails": "Add details",
  "documents.lineQuantity": "Quantity",
  "documents.lineUnit": "Unit",
  "documents.linePrice": "Price",
  "documents.lineDiscount": "Discount %",
  "documents.lineDiscountApplied": "less {percent} discount",
  "documents.lineTax": "Tax rate",
  "documents.lineSupply": "Goods or service",
  "documents.goodsTreatment": "On this document: {treatment}",
  "documents.intraCommunityHint":
    "The goods are an exempt intra-Community supply. Keep proof that they arrived in the other EU country (e.g. an entry certificate) and report the supply in the EC Sales List.",
  "documents.goodsExportHint":
    "The goods are an exempt export delivery. They are exempt only with proof of export (customs document); keep it.",
  "documents.lineTaxRate": "{tax} rate",
  "documents.lineAmount": "Amount",
  "documents.lineActions": "Actions",
  "documents.addLine": "Add line",
  "documents.addTextLine": "Add text line",
  "documents.moveUp": "Move line {number} up",
  "documents.moveDown": "Move line {number} down",
  "documents.removeLine": "Remove line {number}",
  "documents.tooManyLines": "A document has at most {max} lines.",

  "documents.sectionConditions": "Discount and tax",
  "documents.discountKind": "Overall discount",
  "documents.discountPercent": "as a percentage",
  "documents.discountAmount": "as an amount in {currency}",
  "documents.discountValuePercent": "Discount in %",
  "documents.discountValueAmount": "Discount in {currency}",
  "documents.treatment": "Tax treatment",
  "documents.treatmentHint":
    "Determined from the company's and the customer's country. A manual choice is logged.",
  "documents.treatmentAuto": "Automatic",
  "documents.treatmentAutoWith": "Automatic: {treatment}",
  "documents.overrideReason": "Reason",
  "documents.overrideReasonHint": "Why does the tax treatment differ? Stored in the history.",
  "documents.overrideReasonRequired": "Please give a reason for the manual choice.",

  "documents.totals": "Totals",
  "documents.totalsPending": "The totals appear once all entries are valid.",
  "documents.subtotal": "Subtotal",
  "documents.documentDiscount": "Discount",
  "documents.net": "Net",
  "documents.taxGroup": "{tax} {rate} on {amount}",
  "documents.rounding": "Rounding",
  "documents.total": "Total",
  "documents.paid": "Paid",
  "documents.openAmount": "Open",

  "documents.issue": "Issue",
  "documents.issueTitle": "Issue the invoice?",
  "documents.issueDescription":
    "The invoice gets its final number and can no longer be changed. Corrections are only possible with a cancellation invoice.",
  "documents.send": "Mark as sent",
  "documents.sendTitle": "Mark the quote as sent?",
  "documents.sendDescription":
    "The quote gets its number and can no longer be changed. Changes require a new quote (duplicate).",
  "documents.issuedToast": "Invoice {number} issued.",
  "documents.sentToast": "Quote {number} marked as sent.",
  "documents.draftSaved": "Draft saved.",
  "documents.leaveConfirm": "There are unsaved changes. Leave the page anyway?",
  "documents.moreActions": "More actions",
  "documents.duplicate": "Duplicate",
  "documents.duplicatedToast": "Copy created as a draft.",
  "documents.convert": "Turn into invoice",
  "documents.convertedToast": "Invoice draft created from the quote.",
  "documents.accept": "Accepted",
  "documents.acceptedToast": "Quote marked as accepted.",
  "documents.decline": "Declined",
  "documents.declinedToast": "Quote marked as declined.",
  "documents.cancel": "Cancel invoice",
  "documents.cancelTitle": "Cancel the invoice?",
  "documents.cancelDescription":
    "A cancellation invoice with its own number is issued that reverses this invoice. This cannot be undone.",
  "documents.cancelledToast": "Invoice cancelled, cancellation invoice issued.",
  "documents.deleteDraft": "Delete draft",
  "documents.deleteTitle": "Delete the draft?",
  "documents.deleteDescription":
    "The draft is deleted permanently. Issued documents are always kept.",
  "documents.deletedToast": "Draft deleted.",
  "documents.openCancellation": "Open cancellation invoice",
  "documents.openCancelledInvoice": "Open cancelled invoice",
  "documents.openSource": "Open source document",
  "documents.openInvoice": "Open invoice draft",
  "documents.openConvertedInvoice": "Open invoice",
  "documents.history": "History",

  "documents.customerVatMissing":
    "The customer is a business in the EU without a VAT number. With one: {withNumber}; without: {treatment}.",
  "documents.addCustomerVat": "Add the customer's VAT number",
  "documents.markPaid": "Mark as paid",
  "documents.markedPaid": "Marked as paid.",
  "documents.addPayment": "Record payment",
  "documents.addPaymentDescription":
    "Open amount: {amount}. Change the amount for a partial payment.",
  "documents.paidOn": "Payment date",
  "documents.paymentAmount": "Amount in {currency}",
  "documents.paymentNote": "Note",
  "documents.recordPayment": "Save payment",
  "documents.paymentTooHigh": "The amount is higher than the open amount of {amount}.",
  "documents.paymentAdded": "Payment of {amount} recorded.",
  "documents.paymentRemoved": "Payment removed.",
  "documents.payments": "Payments",
  "documents.removePayment": "Remove payment of {amount} from {date}",

  "writeOff.action": "Write off …",
  "writeOff.title": "Write off invoice {number}",
  "writeOff.description":
    "The invoice stays unchanged and the customer receives nothing. It no longer counts as open and is not reminded any more. If the customer pays after all, record the payment as usual.",
  "writeOff.openAmount": "Open amount",
  "writeOff.date": "Written off as of",
  "writeOff.today": "Today",
  "writeOff.endOfInvoiceYear": "End of invoice year",
  "writeOff.dateRange": "The date lies between the invoice date and today.",
  "writeOff.reason": "Reason",
  "writeOff.reason.gone": "Company dissolved or bankrupt",
  "writeOff.reason.time_barred": "Time-barred",
  "writeOff.reason.small_amount": "Small amount",
  "writeOff.reason.untraceable": "Cannot be found",
  "writeOff.reason.other": "Other reason",
  "writeOff.note": "Note",
  "writeOff.submit": "Write off",
  "writeOff.done": "Invoice written off.",
  "writeOff.remove": "Take write-off back",
  "writeOff.removeTitle": "Take the write-off back?",
  "writeOff.removeDescription":
    "The written-off amount is open again afterwards and can be reminded. This is noted in the history.",
  "writeOff.removed": "Write-off taken back.",
  "writeOff.card": "Write-off",
  "writeOff.amount": "Written off",
  "writeOff.writtenOffOn": "As of",
  "writeOff.taxIncluded": "{tax} contained",
  "writeOff.recovered": "Paid later",
  "writeOff.remaining": "Loss",
  "writeOff.afterWriteOff": "Came in after the write-off",
  "writeOff.paymentDescription":
    "Written off: {amount}. The payment takes back that much of the loss.",
  "badDebts.description":
    "Write-offs count in the year of their date, later payments in the year they came in.",
  "badDebts.year": "Year",
  "badDebts.writtenOff": "Written off",
  "badDebts.recovered": "Paid later",
  "badDebts.loss": "Loss",
  "badDebts.net": "Net",
  "settings.tabBackup": "Backup",
  "backup.title": "Backup",
  "backup.description":
    "The database is backed up encrypted: daily and shortly after documents are issued or cancelled.",
  "backup.stateTitle": "Status",
  "backup.state.current": "Current",
  "backup.state.stale": "Out of date",
  "backup.state.not_set_up": "Not set up",
  "backup.notSetUp": "Backups are not set up. If the server breaks, all invoices are lost.",
  "backup.setUpHint":
    "The backup target (e.g. Dropbox, Google Drive, S3 or a NAS) is set in the .env file during installation.",
  "backup.guide": "Open the guide",
  "backup.never": "No successful backup yet.",
  "backup.lastSuccess": "Last successful backup {when}.",
  "backup.lastFailure": "Last attempt failed: {message}",
  "backup.show": "Show backups",
  "backup.check": "Check backup",
  "backup.now": "Back up now",
  "backup.requested": "Backup requested.",
  "backup.pending": "Requested, starts shortly.",
  "backup.running": "Backup running …",
  "backup.unknown": "The backup state cannot be retrieved right now.",
  "backup.runs": "Recent backups",
  "backup.startedAt": "Time",
  "backup.reasonTitle": "Reason",
  "backup.result": "Result",
  "backup.added": "Newly saved",
  "backup.reason.daily": "Daily",
  "backup.reason.document": "After issuing or cancelling",
  "backup.reason.manual": "Manual",
  "backup.status.running": "Running …",
  "backup.status.succeeded": "Succeeded",
  "backup.status.failed": "Failed",
  "backup.status.not_configured": "Not set up",
  "backup.retention":
    "Kept: 7 daily, 4 weekly, 12 monthly and all yearly backups. The guide describes how to restore.",
  "error.backup_not_set_up": "Backups are not set up.",
  "error.backup_folder_unusable": "This folder cannot be used.",
  "error.backup_by_container": "Backups are set up in the backup container here (see the guide).",
  "backup.folder": "Backups go to {folder}.",
  "backup.changeFolder": "Change folder",
  "backup.firstBackup":
    "The first backup starts shortly, once the company details are set up (Settings → Company). After that you can back up here at any time.",
  "backup.folderUnusable": "This folder cannot be used: {reason}",
  "backup.setup.title": "Set up backups",
  "backup.setup.changeTitle": "Choose another folder",
  "backup.setup.description":
    "Preferably a folder a cloud service keeps in sync. Then a copy lives outside this computer.",
  "backup.setup.folders": "Folder for the backups",
  "backup.setup.found": "found",
  "backup.setup.other": "Another folder …",
  "backup.setup.otherHint": "For example a network drive or a USB disk.",
  "backup.setup.path": "Folder path",
  "backup.setup.pathHint":
    "The full path, e.g. E:\\Rhinvoice-Backup or /Volumes/Backup/Rhinvoice. A missing folder is created.",
  "backup.setup.pathMissing": "Please enter the path of a folder.",
  "backup.setup.continue": "Continue to the key",
  "backup.setup.change": "Use this folder",
  "backup.setup.changed": "The next backups go to the new folder.",
  "backup.key.title": "Your recovery key",
  "backup.key.description":
    "It encrypts the backups. Without it no backup can be restored. Keep it apart from this computer, in a password manager or printed.",
  "backup.key.label": "Recovery key",
  "backup.key.kept": "I have stored the key safely.",
  "backup.key.finish": "Set up backups",
  "backup.key.done": "Backups set up. The first one starts shortly.",
  "backup.key.show": "Show key",
  "backup.key.hide": "Hide key",
  "import.title.customers": "Import customers",
  "import.title.articles": "Import articles",
  "import.description":
    "Choose a file, check the mapping, look at the result. Only «Import» saves.",
  "import.chooseFile": "Choose file",
  "import.otherFile": "Other file",
  "import.resultRows": "Rows of the file",
  "import.missingColumns": "Map the columns: a column for {columns} is missing.",
  "import.or": " or ",
  "import.unnamedColumn": "Column {number}",
  "import.deleteTemplateTitle": "Delete template?",
  "import.deleteTemplateDescription": "The template «{name}» is deleted. Imported data stays.",
  "import.problem.unknown_country_prefix": "unknown country prefix before the postal code",
  "import.open": "Import",
  "import.run.customers": {
    zero: "No customers to import",
    one: "Import 1 customer",
    other: "Import {count} customers",
  },
  "import.run.articles": {
    zero: "No articles to import",
    one: "Import 1 article",
    other: "Import {count} articles",
  },
  "import.done.customers": {
    zero: "No customers imported.",
    one: "1 customer imported.",
    other: "{count} customers imported.",
  },
  "import.done.articles": {
    zero: "No articles imported.",
    one: "1 article imported.",
    other: "{count} articles imported.",
  },
  "import.choose.customers": "CSV or Excel file with customers",
  "import.choose.articles": "CSV or Excel file with articles",
  "import.formats":
    "CSV (UTF-8 or Windows-1252), Excel .xlsx and .xls, at most 5 MB and {rows} rows.",
  "import.rows": { zero: "no rows", one: "1 row", other: "{count} rows" },
  "import.settings": "Settings",
  "import.template": "Template",
  "import.templateHint": "A saved column mapping, e.g. for a program used before.",
  "import.templateAuto": "Detected automatically",
  "import.saveTemplate": "Save as template",
  "import.saveTemplateHint":
    "The mapping is saved under this name and fits files with the same column names.",
  "import.templateName": "Template name",
  "import.templateSaved": "Template saved.",
  "import.templateDeleted": "Template deleted.",
  "import.deleteTemplate": "Delete template «{name}»",
  "import.duplicates": "If the entry exists already",
  "import.duplicates.skip": "Skip",
  "import.duplicates.update": "Update",
  "import.duplicates.create": "Create anew",
  "import.duplicatesHint.customers":
    "The same means: same customer number, without a number the same name and postal code. Empty cells change nothing when updating.",
  "import.duplicatesHint.articles":
    "The same means: same article number, without a number the same name. Empty cells change nothing when updating.",
  "import.mapping": "Map columns ({mapped} of {total} mapped)",
  "import.required.customers":
    "Needed: a column for the customer number or the name (company or last name). New customers also need postal code and city.",
  "import.required.articles":
    "Needed: a column for the article number or the name. New articles need a name.",
  "import.mappingCaption": "Column mapping",
  "import.column": "Column in the file",
  "import.example": "Example",
  "import.becomes": "Becomes",
  "import.fieldFor": "Field for the column {column}",
  "import.skipColumn": "Do not import",
  "import.field.number": "Number",
  "import.field.company_name": "Company name",
  "import.field.salutation": "Salutation",
  "import.field.first_name": "First name",
  "import.field.last_name": "Last name",
  "import.field.contact_person": "Contact person",
  "import.field.street": "Street (with number)",
  "import.field.house_number": "House number",
  "import.field.supplement": "Address supplement",
  "import.field.postal_code": "Postal code",
  "import.field.city": "City",
  "import.field.postal_city": "Postal code and city",
  "import.field.country": "Country",
  "import.field.email": "Email",
  "import.field.phone": "Phone",
  "import.field.vat_number": "VAT number",
  "import.field.buyer_reference": "Leitweg-ID / buyer reference",
  "import.field.group": "Group",
  "import.field.default_currency": "Currency",
  "import.field.default_language": "Language",
  "import.field.default_discount": "Discount in %",
  "import.field.payment_terms_days": "Payment terms in days",
  "import.field.tax_exempt": "Tax exempt",
  "import.field.customer_since": "Customer since",
  "import.field.note": "Note",
  "import.field.name": "Name",
  "import.field.description": "Description",
  "import.field.unit": "Unit",
  "import.field.price": "Price (company currency)",
  "import.field.price_chf": "Price CHF",
  "import.field.price_eur": "Price EUR",
  "import.field.tax_category": "VAT rate",
  "import.field.supply": "Goods/service",
  "import.result": "Result of the test run",
  "import.summary": "{new} new · {updated} updated · {skipped} skipped · {error} with errors",
  "import.filter.label": "Show rows",
  "import.filter.all": "All",
  "import.status.new": "New",
  "import.status.updated": "Updated",
  "import.status.skipped": "Skipped",
  "import.status.error": "Error",
  "import.report": "Download report (CSV)",
  "import.reportFile.customers": "import-customers.csv",
  "import.reportFile.articles": "import-articles.csv",
  "import.noRows": "No rows in this view.",
  "import.moreRows": "The first {shown} of {total} rows; the report holds all of them.",
  "import.errorsLeftOut": "Rows with errors are not imported.",
  "import.row": "Row",
  "import.statusTitle": "Result",
  "import.number": "No.",
  "import.name": "Name",
  "import.detail.customers": "Postal code and city",
  "import.detail.articles": "Price",
  "import.problemTitle": "Note",
  "import.problemText": "{field}: {reason}",
  "import.problemValue": "{field} «{value}»: {reason}",
  "import.problem.required": "missing",
  "import.problem.invalid": "invalid",
  "import.problem.invalid_amount": "not an amount",
  "import.problem.invalid_number": "not a whole number",
  "import.problem.invalid_date": "not a date",
  "import.problem.invalid_flag": "neither yes nor no",
  "import.problem.unknown_unit": "unknown unit",
  "import.problem.unknown_tax_rate": "unknown VAT rate",
  "import.problem.unknown_supply": "neither goods nor a service",
  "import.problem.unknown_language": "language not supported (German, English, French or Italian)",
  "import.problem.unknown_currency": "currency not supported (CHF or EUR)",
  "import.doneTitle": "Import finished",
  "import.show.customers": "Go to customers",
  "import.show.articles": "Go to articles",
  "import.again": "Import another file",
  "import.title.invoices": "Take over old invoices",
  "import.demo.title": "Importing is switched off in the demo",
  "import.demo.text":
    "All visitors share this demo, so it takes no files. Install Rhinvoice on your own computer or server – with Docker it takes a few minutes. There you can bring over customers, articles and old invoices with their PDFs.",
  "import.demo.install": "Installation guide",
  "import.demo.guide": "How importing works",
  "import.descriptionInvoices":
    "Invoices of a program used before, with their original PDFs. They stay as they were: locked, with number, amount and PDF.",
  "import.run.invoices": {
    zero: "No invoices to take over",
    one: "Take over 1 invoice",
    other: "Take over {count} invoices",
  },
  "import.done.invoices": {
    zero: "No invoices taken over.",
    one: "1 invoice taken over.",
    other: "{count} invoices taken over.",
  },
  "import.choose.invoices": "List of the invoices (CSV or Excel)",
  "import.required.invoices":
    "Needed are invoice number, date, amount and customer number or name. Rhinvoice finds the column of the numbers by the PDF files.",
  "import.reportFile.invoices": "import-invoices.csv",
  "import.detail.invoices": "Amount",
  "import.show.invoices": "Go to the invoices",
  "import.skippedInvoices":
    "Invoices whose number exists already are skipped: an issued invoice never changes.",
  "import.customer": "Customer",
  "import.pdf": "PDF",
  "import.noPdf": "no PDF",
  "import.attachments": {
    zero: "no attachments",
    one: "+ 1 attachment",
    other: "+ {count} attachments",
  },
  "import.newCustomer": "Customer created (archived)",
  "import.pdfs.title": "PDF files",
  "import.pdfs.description":
    "Choose the folder of the invoices (with its subfolders) or single PDF and ZIP files. Rhinvoice looks for the invoice number in the file name; further files of the same number (reminders, copies) become attachments.",
  "import.pdfs.none": "No PDF files chosen yet; the invoices then come without PDF.",
  "import.pdfs.chosen": {
    zero: "No PDF files in the chosen folder",
    one: "1 PDF file · {size}",
    other: "{count} PDF files · {size}",
  },
  "import.pdfs.folder": "Choose folder",
  "import.pdfs.files": "Choose files",
  "import.pdfs.clear": "Remove the PDF files",
  "import.pdfs.tooMany":
    "More than {max} files: please pack the folder as ZIP file and choose that.",
  "import.pdfs.tooLarge": "Larger than 200 MB together: please take them over in several parts.",
  "import.unused": {
    zero: "Every file belongs to an invoice",
    one: "1 file belongs to no invoice of the list",
    other: "{count} files belong to no invoice of the list",
  },
  "import.unusedHint":
    "Is an invoice missing in the list, or its number not in the file name? A PDF can also be added on the invoice later.",
  "import.nextNumber": "The next invoice you write in Rhinvoice gets the number {number}.",
  "import.problem.non_positive_total": "amount is zero or negative",
  "import.problem.net_above_total": "net larger than the total",
  "import.problem.open_above_total": "open amount larger than the total",
  "import.field.issue_date": "Invoice date",
  "import.field.due_date": "Due date",
  "import.field.customer_number": "Customer number",
  "import.field.customer_name": "Customer name",
  "import.field.currency": "Currency",
  "import.field.net_amount": "Net amount",
  "import.field.tax_amount": "Tax amount",
  "import.field.total_amount": "Total amount",
  "import.field.open_amount": "Open amount",
  "import.field.subject": "Subject",
  "import.field.total": "Total amount",
  "import.field.net": "Net amount",
  "error.unreadable_file": "The file cannot be read. Is it a CSV, Excel or ZIP file?",
  "error.empty_file": "The file is empty.",
  "error.file_too_large":
    "The file is too large: lists at most 5 MB, a PDF at most 20 MB, all PDFs together at most 200 MB.",
  "error.not_a_pdf": "This is no PDF file.",
  "error.too_many_files":
    "Too many files at once. Please take them over as ZIP file or in several parts.",
  "error.imported_without_pdf": "This invoice was taken over without PDF so far.",
  "error.imported_document":
    "A taken over invoice consists of its PDF; this is not possible with it.",
  "error.not_imported": "Files can only be attached to invoices taken over.",
  "error.too_many_rows": "The file has more than 10,000 rows. Please split it into several files.",
  "error.too_many_columns": "The file has more than 100 columns.",
  "error.company_not_set_up": "Please set up the company first.",
  "error.mapping_incomplete": "Columns are missing without which no row can be imported.",
  "error.invalid_mapping": "The column mapping is invalid.",
  "error.column_used_twice": "A column is mapped to two fields.",
  "error.unknown_column": "The mapping names a column that does not exist.",
  "error.unknown_field": "The mapping names an unknown field.",
  "error.empty_template": "The template maps no column.",
  "wizard.title": "Set up Rhinvoice",
  "wizard.steps": "Setup steps",
  "wizard.stepCountry": "Country",
  "wizard.stepCompany": "Company",
  "wizard.stepDone": "(done)",
  "paymentCode.swiss_qr_bill": "QR-bill",
  "paymentCode.epc": "EPC QR code",
  "wizard.countryTitle": "Where is your company?",
  "wizard.countryHint":
    "The country decides the VAT rates, mandatory details, rounding and the payment QR code on invoices.",
  "wizard.smallBusinessHint": "Invoices without VAT, with the required notice.",
  "wizard.later": "Set up later",
  "wizard.next": "Next",
  "wizard.back": "Back",
  "wizard.finish": "Save and continue",
  "wizard.changeLater": "Everything can be changed later in the settings.",
  "wizard.companyTitle": "Your company",
  "wizard.companyHint": "Name and address are the sender on every invoice.",
  "wizard.taxNumberHint": "From the tax office, if there is no VAT ID.",
  "wizard.saved": "Company saved. The next steps are on the overview.",
  "onboarding.title": "Welcome to Rhinvoice",
  "onboarding.progress": "Setup · {done} of {total} done",
  "onboarding.done": "done",
  "onboarding.open": "open",
  "onboarding.dismiss": "Hide checklist",
  "onboarding.dismissed": "Checklist hidden.",
  "onboarding.step.company": "Country and company",
  "onboarding.step.bank_account": "Add a bank account",
  "onboarding.step.numbering": "Check the number sequences",
  "onboarding.step.customers": "Add or import customers",
  "onboarding.step.first_invoice": "Write the first invoice",
  "onboarding.step.backup": "Set up backups",
  "onboarding.hint.company": "Sender, VAT rates and mandatory details of the invoices.",
  "onboarding.hint.bank_account": "For the payment QR code: QR-bill or EPC QR code.",
  "onboarding.hint.numbering":
    "Coming from another program? The next invoice number can continue from there.",
  "onboarding.hint.customers": "One by one or from a CSV or Excel file.",
  "onboarding.hint.first_invoice": "With live preview; it is issued only when you say so.",
  "onboarding.hint.backup": "So that no invoice gets lost, e.g. to Dropbox.",
  "onboarding.action.company": "Set up",
  "onboarding.action.bank_account": "Add bank account",
  "onboarding.action.numbering": "Check",
  "onboarding.action.import": "Import",
  "onboarding.action.customers": "Add customer",
  "onboarding.action.first_invoice": "Write invoice",
  "onboarding.action.backup": "Show backups",
  "settings.tabPrint": "Print template",
  "printLayout.title": "Print template",
  "printLayout.description": "How invoices, quotes and cancellations look on paper and as PDF.",
  "printLayout.template": "Template",
  "printLayout.template.classic": "Classic",
  "printLayout.template.modern": "Modern",
  "printLayout.template.plain": "Plain",
  "printLayout.templateHint.classic": "A rule under the head, a grey table header",
  "printLayout.templateHint.modern": "Company colour as accent, total highlighted",
  "printLayout.templateHint.plain": "Much white space, no colour",
  "printLayout.logoAndFont": "Logo and font",
  "printLayout.logoPosition": "Logo",
  "printLayout.logoPosition.left": "Left",
  "printLayout.logoPosition.right": "Right",
  "printLayout.logoSize": "Logo size",
  "printLayout.logoSize.small": "Small",
  "printLayout.logoSize.medium": "Medium",
  "printLayout.logoSize.large": "Large",
  "printLayout.font": "Font",
  "printLayout.font.sans": "Sans serif (Inter)",
  "printLayout.font.serif": "Serif (Source Serif)",
  "printLayout.colourHint": "Logo and company colour are set under Settings → Company.",
  "printLayout.content": "Content",
  "printLayout.senderLine": "Sender line above the address",
  "printLayout.senderLineHint": "The small line in the envelope window.",
  "printLayout.contact": "Contact details in the head",
  "printLayout.contactHint": "Phone, email and website below the address at the top right.",
  "printLayout.positionNumbers": "Position numbers",
  "printLayout.positionNumbersHint": "The “Pos.” column of the table.",
  "printLayout.footer": "Footer",
  "printLayout.footerAccounts": "Bank details",
  "printLayout.footerAccounts.all": "All accounts",
  "printLayout.footerAccounts.invoice_currency": "Invoice currency only",
  "printLayout.footerAccounts.none": "None",
  "printLayout.footerHint":
    "Accounts of one bank are listed together. Third-party accounts appear only on documents paid to them. The payment QR code always uses the account the invoice is paid to.",
  "printLayout.reset": "Reset",
  "printLayout.issuedStay": "Applies to new PDFs. Issued documents keep their PDF.",
  "printLayout.preview": "Preview of the print template",
  "printLayout.previewHint": "Preview with the latest invoice.",
  "settings.tabDunning": "Dunning",
  "dunning.title": "Dunning",
  "dunning.description": "Overdue invoices, the reminders written and the next step.",
  "dunning.settings": "Set up dunning levels",
  "dunning.emptyTitle": "Nothing overdue",
  "dunning.emptyDescription": "As soon as an invoice passes its due date, it appears here.",
  "dunning.table": "Overdue invoices",
  "dunning.invoice": "Invoice",
  "dunning.customer": "Customer",
  "dunning.open": "Open",
  "dunning.overdue": "Overdue",
  "dunning.reminders": "Written",
  "dunning.next": "Next step",
  "dunning.days": { zero: "Since today", one: "For 1 day", other: "For {count} days" },
  "dunning.none": "None yet",
  "dunning.level.1": "Payment reminder",
  "dunning.level.2": "Second reminder",
  "dunning.level.3": "Final reminder",
  "dunning.done":
    "All levels written. Further steps (e.g. debt collection) happen outside Rhinvoice.",
  "dunning.write": "Write {level}",
  "dunning.written": "{level} written.",
  "dunning.openPdf": "Open PDF",
  "dunning.dueSince": "Due since {date}",
  "dunning.dueFrom": "Due from {date}",
  "dunning.confirmTitle": "Write {level} for {number}?",
  "dunning.confirmEarly": "This level is due only from {date}.",
  "dunning.confirmDetails":
    "To {customer}, new deadline {deadline}, dunning fee {fee}. The reminder stays in the invoice's history.",
  "dunning.problems": "Please correct the marked fields.",
  "dunning.problem.days": "Whole number from 0 to 365",
  "dunning.problem.paymentDays": "Whole number from 1 to 365",
  "dunning.problem.fee": "Amount up to 1000 in steps of 0.05",
  "dunning.problem.text": "At most 2000 characters",
  "eInvoice.beforeEInvoices": "The PDF was created before Rhinvoice embedded e-invoices.",
  "eInvoice.issueWarning": "This invoice will be issued without e-invoice. Missing:",
  "eInvoice.issueXRechnungWarning": "Missing for the XRechnung (German authorities):",
  "eInvoice.xrechnungSummary": "XRechnung for German authorities",
  "eInvoice.title": "E-invoice",
  "eInvoice.embedded":
    "The PDF holds the e-invoice (ZUGFeRD, EN 16931): accounting software reads the data directly.",
  "eInvoice.pending": "The e-invoice is embedded when the PDF is created (ZUGFeRD, EN 16931).",
  "eInvoice.notEmbedded": "The archived PDF holds no e-invoice.",
  "eInvoice.disabled": "The e-invoice was switched off in the print template when this was issued.",
  "eInvoice.missing": "Missing for an e-invoice when it was issued:",
  "eInvoice.xrechnung": "XRechnung (XML)",
  "eInvoice.xrechnungHint":
    "For German authorities; the Leitweg-ID comes from the customer or the invoice's reference.",
  "eInvoice.xrechnungMissing": "Missing for an XRechnung when it was issued:",
  "eInvoice.nextTime":
    "Issued invoices stay as they are. Please add the details for future invoices.",
  "eInvoice.problem.seller_id_missing":
    "UID, VAT ID or commercial register entry (Settings → Company)",
  "eInvoice.problem.seller_vat_id_missing": "VAT number (Settings → Company)",
  "eInvoice.problem.seller_tax_id_missing": "VAT number or tax number (Settings → Company)",
  "eInvoice.problem.buyer_vat_id_missing": "VAT number with country code at the customer",
  "eInvoice.problem.buyer_reference_missing":
    "Leitweg-ID at the customer or reference of the invoice",
  "eInvoice.problem.seller_email_missing": "E-mail (Settings → Company)",
  "eInvoice.problem.seller_phone_missing": "Phone (Settings → Company)",
  "eInvoice.problem.buyer_email_missing": "E-mail at the customer",
  "eInvoice.problem.bank_account_missing": "Bank account (Settings → Bank accounts)",
  "printLayout.eInvoiceTitle": "E-invoice",
  "printLayout.eInvoice": "E-invoice in the PDF (ZUGFeRD)",
  "printLayout.eInvoiceHint":
    "Invoices and cancellations issued from now on carry their data machine-readable as well (EN 16931). The PDF looks the same.",
  "error.e_invoice_incomplete": "Details are missing for an XRechnung.",
  "error.not_an_e_invoice": "Only issued invoices and cancellations are e-invoices.",
  "documents.reminders": "Reminders",
  "dunning.settingsTitle": "Dunning levels",
  "dunning.settingsDescription":
    "Three levels: payment reminder, second and final reminder. Each becomes due after a number of days and sets a new deadline.",
  "dunning.daysAfterInvoice": "Days after the due date",
  "dunning.daysAfterReminder": "Days after the last deadline",
  "dunning.paymentDays": "New deadline in days",
  "dunning.fee": "Dunning fee",
  "dunning.feeHint":
    "In the invoice's currency, without VAT. Only with an agreement or proven costs; 0 for none.",
  "dunning.textDe": "Text (German)",
  "dunning.textEn": "Text (English)",
  "dunning.textFr": "Text (French)",
  "dunning.textIt": "Text (Italian)",
  "dunning.textHint":
    "Placeholders: {faellig} (new deadline), {betrag}, {waehrung}, {nummer}, {kunde}.",
  "error.not_remindable": "This invoice is not overdue or has had all reminders.",
  "error.levels_incomplete": "All three dunning levels are needed.",
  "recurring.short": "Recurring",
  "recurring.title": "Recurring invoices",
  "recurring.description": "Invoices that Rhinvoice writes by itself every month, quarter or year.",
  "recurring.new": "New plan",
  "recurring.newDescription": "The invoice below is written anew on every date.",
  "recurring.emptyTitle": "No recurring invoices yet",
  "recurring.emptyDescription":
    "For hosting, maintenance, subscriptions or flat fees: set up the invoice once, Rhinvoice writes it on every date.",
  "recurring.name": "Name",
  "recurring.nameHint": "Only for you, e.g. «Hosting Holzwerk».",
  "recurring.customer": "Customer",
  "recurring.interval": "Interval",
  "recurring.intervalOption.monthly": "Monthly",
  "recurring.intervalOption.quarterly": "Quarterly",
  "recurring.intervalOption.half_yearly": "Half-yearly",
  "recurring.intervalOption.yearly": "Yearly",
  "recurring.nextDate": "Next invoice",
  "recurring.amount": "Amount",
  "recurring.action": "On the date",
  "recurring.actionShort.draft": "Draft",
  "recurring.actionShort.issue": "Issue",
  "recurring.actionShort.send": "Issue and send",
  "recurring.actionOption.draft": "Create a draft",
  "recurring.actionOption.issue": "Issue",
  "recurring.actionOption.send": "Issue and send by e-mail",
  "recurring.actionHint.draft": "The draft waits under Invoices until you check and issue it.",
  "recurring.actionHint.issue": "The invoice is issued with a number; you send it yourself.",
  "recurring.actionHint.send": "The invoice is issued and sent to the customer's e-mail address.",
  "recurring.state": "Status",
  "recurring.stateLabel.active": "Active",
  "recurring.stateLabel.paused": "Paused",
  "recurring.stateLabel.finished": "Finished",
  "recurring.stateLabel.problem": "Problem",
  "recurring.create": "Create plan",
  "recurring.created": "Plan created.",
  "recurring.planTitle": "Repetition",
  "recurring.planDescription": "When the invoice is written and what happens then.",
  "recurring.startDate": "First invoice on",
  "recurring.startDateHint":
    "Each further one follows at the interval from this day (the 31st stays the month's end).",
  "recurring.endDate": "Last invoice by",
  "recurring.endDateHint": "Leave empty until you end the plan.",
  "recurring.onTheDay": "On the date",
  "recurring.mailNotConfigured": "Sending e-mails is not set up yet.",
  "recurring.servicePeriod": "Fill in the service period (e.g. 1–31 October)",
  "recurring.moreHint": "Subject, reference, currency, language, discount",
  "recurring.field.name": "Name",
  "recurring.field.startDate": "First invoice on",
  "recurring.field.endDate": "Last invoice by",
  "recurring.problem.required": "Please fill in.",
  "recurring.problem.endBeforeStart": "The end is before the first invoice.",
  "recurring.statusTitle": "Status",
  "recurring.finished": "The plan has ended: no further invoice follows.",
  "recurring.next": "Next invoice on {date}.",
  "recurring.nextWithPeriod": "Next invoice on {date}, service period until {to}.",
  "recurring.customerArchived":
    "The customer is archived, but the plan still invoices them. Pause the plan if that is no longer right.",
  "recurring.overriddenSource":
    "The invoice had a tax treatment chosen by hand. Recurring invoices determine it automatically; check the preview.",
  "recurring.pausedHint": "Paused: dates during the pause are skipped.",
  "recurring.noRuns": "No invoice written yet.",
  "recurring.runs": "Invoices written",
  "recurring.outcome.draft": "Draft",
  "recurring.outcome.issued": "Issued",
  "recurring.outcome.sent": "Sent",
  "recurring.outcome.failed": "Failed",
  "recurring.openInvoice": "Open invoice",
  "recurring.customerEmailMissing": "Not sent: the customer has no e-mail address.",
  "recurring.interrupted": "Stopped before the invoice was written.",
  "recurring.interruptedAfterCreate":
    "Stopped after the invoice was created. Please check the invoice.",
  "recurring.pause": "Pause",
  "recurring.resume": "Resume",
  "recurring.paused": "Plan paused.",
  "recurring.resumed": "Plan resumed.",
  "recurring.delete": "Delete plan",
  "recurring.deleteTitle": "Delete plan «{name}»?",
  "recurring.deleteDescription":
    "No further invoices are written. Invoices already written stay as they are.",
  "recurring.deleted": "Plan «{name}» deleted.",
  "recurring.makeRecurring": "Make recurring",
  "error.plan_start_in_past":
    "The first invoice cannot be in the past. A new interval needs a new first day (today or later).",
  "error.plan_not_paused": "The plan is not paused.",
  "mail.sendAction": "Send by e-mail",
  "mail.sendTitle": "Send {title} by e-mail",
  "mail.sendDescription":
    "Check the recipients and the text. The PDF is attached and the mail noted in the history.",
  "mail.notConfigured":
    "Sending e-mails is not set up yet. Enter the mail server of your e-mail provider once.",
  "mail.setUp": "Set up e-mail",
  "mail.to": "To",
  "mail.toHint": "Separate several addresses with commas.",
  "mail.noCustomerAddress":
    "The customer has no e-mail address. Separate several addresses with commas.",
  "mail.subject": "Subject",
  "mail.body": "Message",
  "mail.attachments": "Attachment",
  "mail.attachXrechnung": "Also attach the XRechnung (XML)",
  "mail.copyTo": "A copy goes to {address}.",
  "mail.send": "Send",
  "mail.checkFields": "Please check the marked fields.",
  "mail.cancelledWarning":
    "This invoice is cancelled. Only send it if the customer needs it again.",
  "mail.sending": "Sending …",
  "mail.sent": "E-mail sent to {to}.",
  "mail.recipientMissing": "Please enter at least one e-mail address.",
  "mail.invalidRecipient": "«{address}» is not a valid e-mail address.",
  "mail.tooManyRecipients": "At most {max} recipients.",
  "mail.serverTitle": "Mail server",
  "mail.serverDescription":
    "Rhinvoice sends through the outgoing server (SMTP) of your e-mail provider. Its help pages list the details, e.g. Microsoft 365: smtp.office365.com, port 587, STARTTLS. Gmail and iCloud need an app password.",
  "mail.problems": "Please check the marked fields.",
  "mail.host": "Server",
  "mail.hostHint": "For example smtp.your-provider.com",
  "mail.port": "Port",
  "mail.security": "Encryption",
  "mail.securityOption.starttls": "STARTTLS (usually port 587)",
  "mail.securityOption.tls": "SSL/TLS (usually port 465)",
  "mail.securityOption.none": "None",
  "mail.securityHint.starttls": "Recommended: the connection is encrypted once it is set up.",
  "mail.securityHint.tls": "The connection is encrypted from the start.",
  "mail.securityHint.none": "Not encrypted. Only for a mail server in your own network.",
  "mail.username": "User name",
  "mail.usernameHint": "Usually the e-mail address. Leave empty if the server needs no login.",
  "mail.password": "Password",
  "mail.passwordHint": "Stored and never shown again.",
  "mail.passwordKept": "A password is stored. Leave empty to keep it.",
  "mail.sender": "Sender address",
  "mail.senderHint": "Many providers only send from their own addresses.",
  "mail.senderName": "Sender name",
  "mail.replyTo": "Reply-to address",
  "mail.replyToHint": "Customers' replies go to this address instead of the sender.",
  "mail.copyToSender": "Send a copy of every e-mail to the sender address",
  "mail.problem.required": "Please fill in.",
  "mail.problem.port": "A number between 1 and 65535.",
  "mail.problem.email": "Not a valid e-mail address.",
  "mail.problem.passwordWithoutUser": "A password needs a user name.",
  "mail.testTitle": "Test e-mail",
  "mail.testDescription": "Check the saved settings with a short e-mail.",
  "mail.testFirstSave": "Save the mail server first.",
  "mail.testTo": "Send to",
  "mail.sendTest": "Send test e-mail",
  "mail.testSent": "Test e-mail sent to {to}. Please check the inbox.",
  "mail.templatesTitle": "E-mail templates",
  "mail.templatesDescription":
    "Subject and text of the e-mails that carry documents and reminders. Placeholders are filled in when sending.",
  "mail.templateKind": "For",
  "mail.templateLanguage": "Language",
  "mail.templateOwn": "Your own template.",
  "mail.templateBuiltIn": "Built-in template.",
  "mail.restoreTemplate": "Use the built-in template",
  "mail.templateRestored": "Built-in template restored.",
  "mail.kind.invoice": "Invoice",
  "mail.kind.quote": "Quote",
  "mail.kind.cancellation": "Credit note",
  "mail.kind.reminder_1": "Payment reminder",
  "mail.kind.reminder_2": "Second reminder",
  "mail.kind.reminder_3": "Final reminder",
  "mail.reminderSent": "{level} sent by e-mail",

  "export.open": "Export",
  "export.title": "Export invoices",
  "export.description":
    "The issued invoices and credit notes of a period by invoice date, for your bookkeeping or tax adviser.",
  "export.period": "Period",
  "export.period.month": "Month",
  "export.period.quarter": "Quarter",
  "export.period.year": "Year",
  "export.period.custom": "Custom",
  "export.month": "Month",
  "export.quarter": "Quarter",
  "export.quarterName": "Q{quarter}",
  "export.year": "Year",
  "export.from": "From",
  "export.to": "To",
  "export.problem.missing": "Please enter both days.",
  "export.problem.reversed": "The end lies before the start.",
  "export.problem.tooLong": "At most ten years at a time.",
  "export.format": "Format",
  "export.format.xlsx": "Excel",
  "export.format.csv": "CSV",
  "export.format.pdf": "PDF",
  "export.withPdfs": "Add the invoice PDFs as a ZIP",
  "export.withPdfsHint":
    "One ZIP file with the list and the PDF of every invoice; the column “File” names it.",
  "export.download": "Download",
  "export.preparing": "Preparing …",
  "error.period_reversed": "The period ends before it starts.",
  "error.period_too_long": "A period spans at most ten years.",
  "error.export_busy": "Another export is being prepared. Please try again in a moment.",
};
